import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TextField, TextFieldRef, ValidationResult, ValidationStatus } from 'components/inputs/text-field';
import { useLookups, useSelectedLocation, useSizeTypes } from 'contexts';
import useTimeZone from 'hooks/use-timezone';
import { getBookingByUnitId, getReservedUnitByUnitNumber, getUnitByUnitNumber } from 'services';
import { Chassis, Container, EquipmentType, Genset } from 'types';
import { isSizeTypeUnresolved, sizeTypeReviewNote } from 'types/fleet-files/lookup';
import { keepInspectedValues } from 'utils';

import { useNewTransaction } from '../new-transaction.context';
import useFleetFileUnits from '../use-fleet-file-units';

type InboundUnitProps<T extends Chassis | Container | Genset> = {
  unit: T;
  setUnit: Dispatch<SetStateAction<T> | undefined>;
  required?: boolean;
  customError?: (unitNumber: string) => string;
  textFieldRef?: RefObject<TextFieldRef>;
  equipmentType: EquipmentType;
  ['data-cy']?: string;
};

const InboundUnit = <T extends Chassis | Container | Genset>({
  unit,
  setUnit,
  required,
  textFieldRef,
  equipmentType,
  ...props
}: InboundUnitProps<T>) => {
  const { selectedLocation } = useSelectedLocation();
  const { utcToZonedDate } = useTimeZone();
  const { chassis, container, genset, stack, transaction } = useNewTransaction();
  const { getFleetFileUnit, invalidateFleetFileUnit } = useFleetFileUnits();
  const { getEquipmentType } = useSizeTypes();
  const { unitStatusOptions, unitGradeOptions } = useLookups();
  const saleStatus = unitStatusOptions.find(label => label === 'SU - SALE UNIT');
  const saleGrade = unitGradeOptions.find(label => label === 'AGEMAX');
  const [saleLookup, setSaleLookup] = useState<{
    unitNumber: string;
    locationId?: number;
    equipmentType: EquipmentType;
  }>();
  const scopeRef = useRef({
    unitNumber: unit.unitNumber,
    locationId: selectedLocation?.id,
    equipmentType,
    enabled: selectedLocation?.fleetFileLookupEnabled
  });
  scopeRef.current = {
    unitNumber: unit.unitNumber,
    locationId: selectedLocation?.id,
    equipmentType,
    enabled: selectedLocation?.fleetFileLookupEnabled
  };

  // Catalogs may load after the fleet response. Fill blank fields only, keeping operator edits
  // and saved draft/review values. No save-time status/grade override exists on the server.
  useEffect(() => {
    if (
      !saleLookup ||
      saleLookup.unitNumber !== unit.unitNumber ||
      saleLookup.locationId !== selectedLocation?.id ||
      saleLookup.equipmentType !== equipmentType ||
      selectedLocation?.fleetFileLookupEnabled === false ||
      (!saleStatus && !saleGrade)
    )
      return;
    setUnit(u => {
      if (u.unitNumber !== saleLookup.unitNumber) return u;
      const unitStatus = u.unitStatus || saleStatus;
      const unitGrade = u.unitGrade || saleGrade;
      return unitStatus === u.unitStatus && unitGrade === u.unitGrade ? u : { ...u, unitStatus, unitGrade };
    });
  }, [
    saleLookup,
    saleStatus,
    saleGrade,
    unit.unitNumber,
    selectedLocation?.id,
    selectedLocation?.fleetFileLookupEnabled,
    equipmentType,
    setUnit
  ]);

  /* the unit number most recently submitted for validation, for the stale-response check below */
  const unitNumberRef = useRef<string>('');
  /* the field's showing the unmapped-size-type note, which a size type picked by hand answers */
  const sizeTypeNoteShown = useRef(false);
  const saleNoteShown = useRef(false);
  /* the size type as of the latest render, for a lookup that lands after the operator picked one */
  const sizeTypeRef = useRef(unit.sizeType);
  sizeTypeRef.current = unit.sizeType;
  const ownFieldRef = useRef<TextFieldRef>(null);
  const fieldRef = textFieldRef ?? ownFieldRef;

  useEffect(() => {
    if (unit.sizeType && sizeTypeNoteShown.current) {
      sizeTypeNoteShown.current = false;
      fieldRef.current?.clearAdvisory();
    }
  }, [unit.sizeType, fieldRef]);

  useEffect(() => {
    if (saleStatus && saleGrade && saleNoteShown.current) {
      saleNoteShown.current = false;
      fieldRef.current?.clearAdvisory();
    }
  }, [saleStatus, saleGrade, fieldRef]);

  const allUnitNumbers = useMemo(() => {
    return [chassis, container, genset, ...stack].map(({ unitNumber }) => unitNumber);
  }, [chassis, container, genset, stack]);

  const validateUnit = useCallback(
    async (unitNumber: string): Promise<ValidationResult> => {
      unitNumberRef.current = unitNumber;
      setSaleLookup(undefined);
      sizeTypeNoteShown.current = false;
      saleNoteShown.current = false;
      if (!unitNumber) {
        return { status: ValidationStatus.DEFAULT };
      } else if (allUnitNumbers.filter(un => un === unitNumber).length > 1) {
        return { status: ValidationStatus.WARNING, error: `Duplicate unit number` };
      }

      if (!/^[A-Z0-9]{1,19}$/.test(unitNumber)) {
        return { status: ValidationStatus.WARNING, error: 'Expected: 1-19 alphanumeric characters' };
      }

      const response = await getUnitByUnitNumber(unitNumber);
      if (!response) {
        const reservedUnit = (await getReservedUnitByUnitNumber(unitNumber)) as T | null;
        if (reservedUnit?.id && !reservedUnit.receivedDate) {
          reservedUnit.booking = await getBookingByUnitId(reservedUnit.id);
          if (reservedUnit.location?.id !== selectedLocation?.id) {
            return {
              status: ValidationStatus.WARNING,
              error: `Unit reserved at ${reservedUnit.location?.name} (${reservedUnit.booking?.bookingNumber ?? 'N/A'})`
            };
          }
          if (getEquipmentType(reservedUnit.sizeType) !== equipmentType) {
            return { status: ValidationStatus.WARNING, error: `Unit not ${equipmentType.toLowerCase()}` };
          }
          /* re-validating the unit already here (e.g. back from review) keeps what was recorded on this move */
          setUnit(u => keepInspectedValues(u, reservedUnit));
        } else {
          const requestedFor = { unitNumber, locationId: selectedLocation?.id, equipmentType };
          const queriedOutcome = await getFleetFileUnit(unitNumber, equipmentType);
          const outcome = scopeRef.current.enabled === false ? { kind: 'disabled' as const } : queriedOutcome;

          /*
           * A lookup is slow enough that the operator can type on, or switch terminal, before it
           * lands. Applying a response whose inputs are no longer current writes another unit's
           * details onto the form, so drop it instead.
           */
          const stale =
            requestedFor.unitNumber !== unitNumberRef.current ||
            requestedFor.locationId !== scopeRef.current.locationId ||
            requestedFor.equipmentType !== scopeRef.current.equipmentType ||
            requestedFor.unitNumber !== scopeRef.current.unitNumber;
          if (stale) return { status: ValidationStatus.DEFAULT };

          /* clear any reservation state first, whatever the fleet file turns out to say */
          setUnit(u => {
            const preserveDraftReservation = !!transaction.draft && !!u?.id;
            /*
             * Only a reservation's booking goes with it, and only a reservation gives a gate-in unit a
             * positive id (stack rows use negative keys). A booking the operator entered stays: dropping
             * it on the way back from review also cleared the reefer values it had filled (2026-09-29).
             */
            const reservationBooking = (u?.id ?? 0) > 0;
            return {
              ...u,
              id: preserveDraftReservation ? u.id : null,
              baseUnit: preserveDraftReservation ? u.baseUnit : null,
              booking: preserveDraftReservation || !reservationBooking ? u.booking : null
            };
          });

          /*
           * Everything the fleet file says from here on is advisory (decided 2026-09-23): shown, but
           * never blocking the save. The operator's values win; the server logs any contradiction as
           * an override. The checks above — duplicate, already in yard, reserved elsewhere — still block.
           */
          switch (outcome.kind) {
            case 'disabled':
            case 'notFound':
              /* a genuine miss is a normal result: nothing to apply, nothing to warn about */
              return { status: ValidationStatus.SUCCESS };

            case 'denied':
              return {
                status: ValidationStatus.WARNING,
                error: 'Not permitted to check the fleet file',
                advisory: true
              };

            case 'unavailable':
            case 'timeout':
              /* the provider is down. Do NOT say the unit was not found — it was never asked about. */
              return {
                status: ValidationStatus.WARNING,
                error: 'Fleet file unavailable — enter details manually',
                advisory: true
              };

            case 'invalidData':
              return { status: ValidationStatus.WARNING, error: 'Fleet file returned unusable data', advisory: true };

            case 'invalidRequest':
              return { status: ValidationStatus.SUCCESS };

            case 'found': {
              const record = outcome.record;

              /*
               * Read the class conflict off the issue, not off record.equipmentType.
               *
               * equipmentType is only populated once a mapping has a reviewed catalog target, and on
               * release none of them do - so keying the check on it meant the warning could never
               * appear. The backend now decides the conflict from the mapping's own equipment class
               * when there is no target, and reports it as EQUIPMENT_TYPE_MISMATCH either way.
               */
              const classConflict = record.issues?.find(i => i.code === 'EQUIPMENT_TYPE_MISMATCH');
              if (classConflict) {
                return {
                  status: ValidationStatus.WARNING,
                  error:
                    classConflict.detail ??
                    `Fleet file says this is a ${(
                      classConflict.actual ?? ''
                    ).toLowerCase()}, not a ${equipmentType.toLowerCase()}`,
                  advisory: true
                };
              }

              if (record.toBeSoldFlag === true) setSaleLookup(requestedFor);

              setUnit(u => ({
                ...u,
                customer: record.customer ?? u.customer,
                fmcsaDate: record.fmcsaDate ? new Date(record.fmcsaDate).valueOf() : u.fmcsaDate,
                licensePlate: record.licensePlate ?? u.licensePlate,
                manufacturedDate: utcToZonedDate(record.manufacturedDate) || u.manufacturedDate,
                /*
                 * Only a resolved catalog code is ever applied. On release every mapping is
                 * unresolved by design, so this is normally left for the operator to pick — that is
                 * expected, not a regression. A legacy string must never land here.
                 */
                sizeType: isSizeTypeUnresolved(record) ? u.sizeType : record.sizeType ?? u.sizeType
              }));

              if (record.toBeSoldFlag === true && (!saleStatus || !saleGrade)) {
                saleNoteShown.current = true;
                return {
                  status: ValidationStatus.WARNING,
                  error:
                    'Fleet file marks this unit for sale - select SALE status and AGEMAX grade; active catalog options are unavailable',
                  advisory: true
                };
              }
              if (isSizeTypeUnresolved(record) && !sizeTypeRef.current) {
                sizeTypeNoteShown.current = true;
                /* a deliberate "no single size type" carries the admin's reason; show it over the generic text */
                const note = sizeTypeReviewNote(record);
                return {
                  status: ValidationStatus.WARNING,
                  error: note ? `Fleet file: ${note}` : 'Fleet size type not configured — select one manually',
                  advisory: true
                };
              }
              return { status: ValidationStatus.SUCCESS };
            }
          }
        }
        return { status: ValidationStatus.SUCCESS };
      }

      return { status: ValidationStatus.WARNING, error: 'Unit already in yard' };
    },
    [
      allUnitNumbers,
      equipmentType,
      getEquipmentType,
      getFleetFileUnit,
      selectedLocation,
      saleStatus,
      saleGrade,
      setUnit,
      transaction.draft,
      utcToZonedDate
    ]
  );

  return (
    <TextField
      label="UNIT NUMBER"
      ref={fieldRef}
      required={required}
      value={unit.unitNumber ?? ''}
      onChange={unitNumber => (!unitNumber ? setUnit(undefined) : setUnit(u => ({ ...u, unitNumber })))}
      validate={validateUnit}
      /* ↻ is the operator asking the provider again, so a cached answer must not be replayed (2.5) */
      onRevalidate={() => {
        if (unit.unitNumber) invalidateFleetFileUnit(unit.unitNumber, equipmentType);
      }}
      data-cy={props['data-cy']}
    />
  );
};

export default InboundUnit;
